import { Link } from "@tanstack/react-router";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";

interface LoginMagicLinkProps {
  stage: "enter-email" | "check-email";
  email: string;
  otpCode: string;
  error: string | null;
  requestPending: boolean;
  verifyPending: boolean;
  onEmailChange: (value: string) => void;
  onOtpChange: (value: string) => void;
  onSubmitEmail: (e: React.FormEvent) => void;
  onSubmitOtp: (e: React.FormEvent) => void;
  onUsePassword: () => void;
  onUseDifferentEmail: () => void;
}

/// The magic-link / OTP alternative to password sign-in. Purely presentational:
/// the parent route owns the email/otp state, mutations, and navigation.
export function LoginMagicLink({
  stage,
  email,
  otpCode,
  error,
  requestPending,
  verifyPending,
  onEmailChange,
  onOtpChange,
  onSubmitEmail,
  onSubmitOtp,
  onUsePassword,
  onUseDifferentEmail,
}: LoginMagicLinkProps) {
  if (stage === "enter-email") {
    return (
      <div className="flex flex-col gap-4">
        <form onSubmit={onSubmitEmail} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="you@company.com"
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button type="submit" disabled={requestPending}>
            {requestPending ? "Sending…" : "Email me a one-time code"}
          </Button>
          <button
            type="button"
            className="text-xs text-text-tertiary underline-offset-4 hover:text-text-secondary hover:underline"
            onClick={onUsePassword}
          >
            Sign in with a password instead
          </button>
          <p className="text-xs text-text-tertiary">
            No account?{" "}
            <Link
              to="/signup"
              search={{}}
              className="text-text-brand underline-offset-4 hover:underline"
            >
              Create one
            </Link>
            .
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-border bg-surface-subtle p-4 text-sm">
        <p className="font-medium">Check your email.</p>
        <p className="mt-1 text-text-secondary">
          We sent a link and a 6-digit code to <span className="font-medium">{email}</span>. Click
          the link, or type the code below.
        </p>
        <p className="mt-2 text-xs text-text-tertiary">
          Dev: open the shore terminal, the magic link + code are logged there.
        </p>
      </div>
      <form onSubmit={onSubmitOtp} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="otp">6-digit code</Label>
          <Input
            id="otp"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={otpCode}
            onChange={(e) => onOtpChange(e.target.value.replace(/\D/g, ""))}
            placeholder="000000"
            autoFocus
          />
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <Button type="submit" disabled={verifyPending}>
          {verifyPending ? "Verifying…" : "Verify code"}
        </Button>
        <button
          type="button"
          className="text-xs text-text-tertiary underline-offset-4 hover:text-text-secondary hover:underline"
          onClick={onUseDifferentEmail}
        >
          Use a different email
        </button>
      </form>
    </div>
  );
}
